import * as React from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { ApiError, getJSON, postJSON } from '@/lib/api'
import {
  apiErrors,
  customFieldsForLevel,
  emptyErrors,
  reviewApplication,
  serializeCustomFieldValues,
} from '@/pages/PublicJoinFormPage.helpers'
import { ApplyStep, PublicJoinShell } from '@/pages/PublicJoinFormPage.parts'
import { ReviewStep } from '@/pages/PublicJoinFormPage.review'
import { usePublicJoinFormState } from '@/pages/PublicJoinFormPage.state'
import {
  ConfirmationPanel,
  PublicJoinPaymentConfirmation,
} from '@/pages/PublicJoinFormPage.confirmation'
import type {
  JoinApplicationResponse,
  JoinCustomFieldValue,
  DiscountQuote,
  JoinFormErrors,
  JoinOptionsResponse,
  PersonFieldName,
  PersonForm,
  OrganizationForm,
  PublicJoinCustomField,
  WizardStep,
} from '@/pages/PublicJoinFormPage.types'
import type { Association } from '@/pages/StaffSignInPage'

export default function PublicJoinFormPage({
  association,
  confirmation,
}: {
  association: Association
  confirmation?: { paymentToken: string; sessionId: string }
}) {
  if (confirmation)
    return <PaymentConfirmation association={association} confirmation={confirmation} />
  return <PublicJoinFormController association={association} />
}

// eslint-disable-next-line max-lines-per-function
function PublicJoinFormController({ association }: { association: Association }) {
  const {
    step,
    setStep,
    selectedLevelId,
    setSelectedLevelId,
    person,
    organization,
    website,
    setWebsite,
    customFieldValues,
    errors,
    setErrors,
    submission,
    setSubmission,
    discountCode,
    setDiscountCode,
    discountQuote,
    setDiscountQuote,
    enrollAutopay,
    setEnrollAutopay,
    selectedChapterIds,
    setSelectedChapterIds,
    updatePersonField,
    updateOrganizationField,
    updateCustomField,
  } = usePublicJoinFormState()
  const optionsQuery = useQuery({
    queryKey: ['public-join-options'],
    queryFn: () => getJSON<JoinOptionsResponse>('/api/v1/join/options'),
    retry: false,
    enabled: true,
  })
  const options = optionsQuery.data
  const selectedLevel =
    options?.membership_levels.find((level) => level.id === selectedLevelId) ?? null
  const submitApplication = useSubmitApplication({
    selectedLevelId,
    discountQuote,
    website,
    person,
    organization,
    options,
    customFieldValues,
    selectedChapterIds,
    enrollAutopay,
    setSubmission,
    setErrors,
    setStep,
  })
  const quoteDiscount = useDiscountQuote(
    selectedLevelId,
    discountCode,
    setDiscountCode,
    setDiscountQuote,
  )

  function submitReviewedApplication(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (submitApplication.isPending) return
    submitApplication.mutate()
  }

  return (
    <PublicJoinShell association={association}>
      <JoinPageContent
        association={association}
        options={options}
        optionsLoading={optionsQuery.isLoading}
        optionsError={optionsQuery.isError}
        step={step}
        selectedLevelId={selectedLevelId}
        selectedLevel={selectedLevel}
        person={person}
        organization={organization}
        customFieldValues={customFieldValues}
        website={website}
        errors={errors}
        submission={submission}
        submitting={submitApplication.isPending}
        discountCode={discountCode}
        discountQuote={discountQuote}
        discountPending={quoteDiscount.isPending}
        discountError={discountErrorMessage(quoteDiscount.error)}
        enrollAutopay={enrollAutopay}
        selectedChapterIds={selectedChapterIds}
        onSelectLevel={(levelId) => {
          setSelectedLevelId(levelId)
          setDiscountCode('')
          setDiscountQuote(null)
          quoteDiscount.reset()
          setEnrollAutopay(false)
          setErrors((current) => ({ ...current, membershipLevel: undefined }))
        }}
        onPersonChange={updatePersonField}
        onOrganizationChange={updateOrganizationField}
        onWebsiteChange={setWebsite}
        onCustomFieldChange={updateCustomField}
        onChapterIdsChange={setSelectedChapterIds}
        onDiscountCodeChange={(value) => {
          setDiscountCode(value)
          setDiscountQuote(null)
          quoteDiscount.reset()
        }}
        onApplyDiscount={() => quoteDiscount.mutate()}
        onEnrollAutopayChange={setEnrollAutopay}
        onReview={() =>
          reviewApplication({
            options,
            selectedLevelId,
            person,
            customFieldValues,
            organization,
            setErrors,
            setStep,
          })
        }
        onBack={() => setStep('apply')}
        onSubmit={submitReviewedApplication}
      />
    </PublicJoinShell>
  )
}

function useDiscountQuote(
  selectedLevelId: number | null,
  discountCode: string,
  setDiscountCode: React.Dispatch<React.SetStateAction<string>>,
  setDiscountQuote: React.Dispatch<React.SetStateAction<DiscountQuote | null>>,
) {
  return useMutation({
    mutationFn: () =>
      postJSON<{ quote: DiscountQuote }>('/api/v1/join/discount_quote', {
        quote: { membership_level_id: selectedLevelId, discount_code: discountCode },
      }),
    onSuccess: ({ quote }) => {
      setDiscountQuote(quote)
      setDiscountCode(quote.code)
    },
    onError: () => setDiscountQuote(null),
  })
}

function PaymentConfirmation({
  association,
  confirmation,
}: {
  association: Association
  confirmation: { paymentToken: string; sessionId: string }
}) {
  return (
    <PublicJoinPaymentConfirmation
      association={association}
      paymentToken={confirmation.paymentToken}
      sessionId={confirmation.sessionId}
    />
  )
}

function useSubmitApplication({
  selectedLevelId,
  discountQuote,
  website,
  person,
  organization,
  options,
  customFieldValues,
  selectedChapterIds,
  enrollAutopay,
  setSubmission,
  setErrors,
  setStep,
}: {
  selectedLevelId: number | null
  discountQuote: DiscountQuote | null
  website: string
  person: PersonForm
  organization: OrganizationForm
  options?: JoinOptionsResponse
  customFieldValues: Record<string, JoinCustomFieldValue>
  enrollAutopay: boolean
  selectedChapterIds: number[]
  setSubmission: React.Dispatch<React.SetStateAction<JoinApplicationResponse | null>>
  setErrors: React.Dispatch<React.SetStateAction<JoinFormErrors>>
  setStep: React.Dispatch<React.SetStateAction<WizardStep>>
}) {
  return useMutation({
    mutationFn: () =>
      postJSON<JoinApplicationResponse>('/api/v1/join/applications', {
        application: {
          membership_level_id: selectedLevelId,
          ...(discountQuote ? { discount_code: discountQuote.code } : {}),
          ...(enrollAutopay ? { enroll_autopay: true } : {}),
          website,
          person,
          ...(selectedChapterIds.length > 0 ? { chapter_ids: selectedChapterIds } : {}),
          ...(options?.membership_levels.find((level) => level.id === selectedLevelId)?.kind ===
          'organization'
            ? { organization }
            : {}),
          custom_field_values: serializeCustomFieldValues(
            customFieldsForLevel(
              options ?? { identity_fields: [], membership_levels: [], custom_fields: [] },
              options?.membership_levels.find((level) => level.id === selectedLevelId) ?? null,
            ),
            customFieldValues,
          ),
        },
      }),
    onSuccess: (response) => {
      setSubmission(response)
      setErrors(emptyErrors())
      setStep('confirmation')
    },
    onError: (error: Error) => {
      if (isSilentRejection(error)) {
        setSubmission({ received: true })
        setErrors(emptyErrors())
        setStep('confirmation')
        return
      }

      setErrors(apiErrors(error, options?.custom_fields ?? []))
      setStep('apply')
    },
  })
}

function discountErrorMessage(error: Error | null) {
  if (!(error instanceof ApiError) || typeof error.body !== 'object' || error.body === null)
    return undefined
  const errors = (error.body as { errors?: { discount_code?: string[] } }).errors
  return errors?.discount_code?.[0]
}

function isSilentRejection(error: Error) {
  if (!(error instanceof ApiError) || error.status !== 429) return false
  if (typeof error.body !== 'object' || error.body === null) return false

  return 'received' in error.body && error.body.received === true
}

// eslint-disable-next-line max-lines-per-function
function JoinPageContent({
  association,
  options,
  optionsLoading,
  optionsError,
  step,
  selectedLevelId,
  selectedLevel,
  person,
  organization,
  customFieldValues,
  website,
  errors,
  submission,
  submitting,
  discountCode,
  discountQuote,
  discountPending,
  discountError,
  enrollAutopay,
  selectedChapterIds,
  onChapterIdsChange,
  onSelectLevel,
  onPersonChange,
  onOrganizationChange,
  onWebsiteChange,
  onCustomFieldChange,
  onDiscountCodeChange,
  onApplyDiscount,
  onEnrollAutopayChange,
  onReview,
  onBack,
  onSubmit,
}: {
  association: Association
  options: JoinOptionsResponse | undefined
  optionsLoading: boolean
  optionsError: boolean
  step: WizardStep
  selectedLevelId: number | null
  selectedLevel: JoinOptionsResponse['membership_levels'][number] | null
  person: PersonForm
  organization: OrganizationForm
  customFieldValues: Record<string, JoinCustomFieldValue>
  website: string
  errors: JoinFormErrors
  submission: JoinApplicationResponse | null
  submitting: boolean
  discountCode: string
  discountQuote: DiscountQuote | null
  discountPending: boolean
  discountError?: string
  enrollAutopay: boolean
  selectedChapterIds: number[]
  onSelectLevel: (levelId: number) => void
  onPersonChange: (name: PersonFieldName, value: string) => void
  onOrganizationChange: (name: PersonFieldName, value: string) => void
  onWebsiteChange: (value: string) => void
  onCustomFieldChange: (field: PublicJoinCustomField, value: JoinCustomFieldValue) => void
  onDiscountCodeChange: (value: string) => void
  onApplyDiscount: () => void
  onEnrollAutopayChange: (checked: boolean) => void
  onChapterIdsChange: (ids: number[]) => void
  onReview: () => void
  onBack: () => void
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void
}) {
  if (optionsLoading) {
    return (
      <p className="text-body-sm text-muted-foreground" role="status">
        Loading join form...
      </p>
    )
  }

  if (optionsError || !options) return <Alert>Join form options could not be loaded.</Alert>
  if (step === 'confirmation' && submission) return <ConfirmationPanel submission={submission} />
  if (step === 'review' && selectedLevel) {
    return (
      <ReviewStep
        selectedLevel={selectedLevel}
        organization={organization}
        person={person}
        customFields={customFieldsForLevel(options, selectedLevel)}
        customFieldValues={customFieldValues}
        website={website}
        submitting={submitting}
        discountQuote={discountQuote}
        enrollAutopay={enrollAutopay}
        onBack={onBack}
        onWebsiteChange={onWebsiteChange}
        onEnrollAutopayChange={onEnrollAutopayChange}
        onSubmit={onSubmit}
      />
    )
  }

  return (
    <ApplyStep
      associationName={association.name}
      options={options}
      selectedLevelId={selectedLevelId}
      person={person}
      organization={organization}
      customFieldValues={customFieldValues}
      errors={errors}
      selectedLevel={selectedLevel}
      discountCode={discountCode}
      discountQuote={discountQuote}
      discountPending={discountPending}
      discountError={discountError}
      onSelectLevel={onSelectLevel}
      onPersonChange={onPersonChange}
      onOrganizationChange={onOrganizationChange}
      onCustomFieldChange={onCustomFieldChange}
      chapterIds={selectedChapterIds}
      onChapterIdsChange={onChapterIdsChange}
      onDiscountCodeChange={onDiscountCodeChange}
      onApplyDiscount={onApplyDiscount}
      onReview={onReview}
    />
  )
}
